import type { BlogSection } from './blogPosts';
import type { FaqItem } from '../utils/seoStructuredData';

export interface AlternativeCompetitor {
  slug: string;
  name: string;
  emoji: string;
  /** Short text on hub card */
  hubSummary: string;
  /** One-line positioning vs Retrosive */
  hubPitch: string;
  seoTitle: string;
  seoDescription: string;
  tags: string[];
  /** FAQ schema + long-tail keyword coverage */
  faqs: FaqItem[];
  content: BlogSection[];
}

const STANDARD_RETRO_ROWS: [string, string, string][] = [
  ['Purpose-built for sprint retrospectives', '✅ Yes', 'N/A'],
  ['Real-time collaboration', '✅ Yes', 'N/A'],
  ['Private writing phase', '✅ Yes', 'N/A'],
  ['Anonymous feedback mode', '✅ Yes', 'N/A'],
  ['Built-in retro templates', '✅ 12+', 'N/A'],
  ['Voting & grouping', '✅ Yes', 'N/A'],
  ['Action item tracking', '✅ Yes', 'N/A'],
  ['AI retrospective summary', '✅ Yes', 'N/A'],
  ['Cross-sprint pattern analysis', '✅ Yes', 'N/A'],
  ['Join without account', '✅ Yes', 'N/A'],
  ['Setup time (first retro)', '< 2 minutes', 'N/A'],
];

function withCompetitorColumn(
  rows: [string, string, string][],
  competitorName: string,
  competitorValues: string[]
): [string, string, string][] {
  return rows.map((row, i) => [row[0], row[1], competitorValues[i] ?? row[2]]);
}

function buildComparison(opts: {
  competitorName: string;
  intros: string[];
  featureRows: [string, string, string][];
  focusTitle: string;
  focusParagraphs: string[];
  tip?: string;
  chooseCompetitor: string[];
  chooseRetrosive: string[];
  extraSections?: BlogSection[];
  ctaText?: string;
  ctaLabel?: string;
}): BlogSection[] {
  const sections: BlogSection[] = [
    ...opts.intros.map((text) => ({ type: 'p' as const, text })),
    { type: 'h2', text: 'Feature Comparison' },
    {
      type: 'table',
      headers: ['Feature', 'Retrosive', opts.competitorName],
      rows: opts.featureRows,
    },
    { type: 'h2', text: opts.focusTitle },
    ...opts.focusParagraphs.map((text) => ({ type: 'p' as const, text })),
  ];

  if (opts.tip) {
    sections.push({ type: 'tip', text: opts.tip });
  }

  if (opts.extraSections) {
    sections.push(...opts.extraSections);
  }

  sections.push(
    { type: 'h2', text: `Who Should Choose ${opts.competitorName}?` },
    { type: 'ul', items: opts.chooseCompetitor },
    { type: 'h2', text: 'Who Should Choose Retrosive?' },
    { type: 'ul', items: opts.chooseRetrosive },
    {
      type: 'cta',
      text: opts.ctaText ?? 'Try Retrosive free, no credit card needed',
      href: '/register',
      label: opts.ctaLabel ?? 'Start Free with Retrosive →',
    }
  );

  return sections;
}

export const alternatives: AlternativeCompetitor[] = [
  {
    slug: 'retrosive-vs-easyretro',
    name: 'EasyRetro',
    emoji: '📋',
    hubSummary:
      "EasyRetro gets a column board up in seconds and that's still its whole pitch. We built Retrosive for teams who've outgrown columns and want the AI to write the summary for them.",
    hubPitch: 'Quick columns, or AI that remembers what happened last sprint too.',
    seoTitle: 'Retrosive vs EasyRetro: Which Retrospective Tool Is Better?',
    seoDescription:
      'A practical look at Retrosive vs EasyRetro: what each one actually does with AI, templates, and pricing, and which fits a small team vs a growing eng org.',
    tags: ['EasyRetro alternative', 'EasyRetro vs Retrosive', 'retrospective tool comparison'],
    faqs: [
      {
        question: 'Is Retrosive better than EasyRetro?',
        answer:
          "Depends what you need. If your team runs a retro every sprint and you're tired of writing the recap yourself, Retrosive's AI summary and sprint-over-sprint tracking will save real time. If you retro once a quarter and just need sticky columns, EasyRetro is still fine for that.",
      },
      {
        question: 'Does EasyRetro have AI retrospective summaries?',
        answer:
          "It doesn't, no. EasyRetro is a columns-and-cards board, nothing more. Retrosive writes an AI summary after each session, sentiment included, so nobody has to type it up afterward.",
      },
      {
        question: 'Can I migrate from EasyRetro to Retrosive?',
        answer:
          'Yes, and it takes about two minutes. Spin up a board, pick a template, drop the link in Slack. No account needed on the participant side and no credit card on yours.',
      },
    ],
    content: buildComparison({
      competitorName: 'EasyRetro',
      intros: [
        "EasyRetro (it used to be called FunRetro) has been around since 2018 and a lot of teams still like it for exactly that reason: it's simple, it loads fast, and everyone already knows how it works.",
        "That's fine until you're running retros every two weeks and realize nobody's tracking whether last sprint's action items actually got done. That gap is basically why we built Retrosive.",
      ],
      featureRows: withCompetitorColumn(STANDARD_RETRO_ROWS, 'EasyRetro', [
        '✅ Yes',
        '✅ Yes',
        '✅ Yes',
        '✅ Yes',
        '✅ 15+',
        '✅ Yes',
        '✅ Yes',
        '❌ No',
        '❌ No',
        '✅ Yes',
        '2–3 minutes',
      ]),
      focusTitle: 'The part EasyRetro just doesn\'t do',
      focusParagraphs: [
        "After a Retrosive session ends, you get a written summary automatically: the themes that came up, the mood of the room, the risks, the action items. EasyRetro has none of this, it's still on you to write the recap.",
        "We also keep a history across sprints, so if the same blocker keeps showing up, someone actually notices instead of it quietly repeating for the fourth retro in a row.",
      ],
      tip: "If retros happen occasionally and columns are genuinely all you need, stick with EasyRetro, it's a solid little tool. If you're doing this every sprint, the AI writeup pays for itself pretty fast.",
      chooseCompetitor: [
        'You run retros occasionally, not every sprint',
        "You don't need a summary written for you or trend tracking",
        'You want the simplest possible board and nothing else',
      ],
      chooseRetrosive: [
        'You run a retro every sprint, like clockwork',
        "You're tired of being the one who writes the recap afterward",
        'You want to actually notice when the same issue keeps coming back',
        'Your team is distributed and needs both async and live options',
      ],
      ctaText: 'Skip the manual write-up next sprint: try Retrosive free',
    }),
  },
  {
    slug: 'retrosive-vs-parabol',
    name: 'Parabol',
    emoji: '🔬',
    hubSummary:
      "Parabol's strength is enterprise plumbing, Jira and GitHub sync, SOC 2, the works. Retrosive has the Jira link too, but leads with AI summaries and a much faster first-retro experience.",
    hubPitch: 'Compliance-heavy meeting stack, or AI-first retros that start in two minutes.',
    seoTitle: 'Retrosive vs Parabol: AI, Integrations, and Ease of Use',
    seoDescription:
      'How Retrosive and Parabol actually differ: AI summaries, Jira integration, SOC 2, setup time, and which one fits your agile team better.',
    tags: ['Parabol alternative', 'Parabol vs Retrosive', 'agile retrospective tools'],
    faqs: [
      {
        question: 'Does Retrosive integrate with Jira like Parabol?',
        answer:
          'It does. Retrosive pulls live sprint issues, velocity, and burndown into the board itself. Parabol goes a step further and pushes action items back out into Jira and GitHub tickets automatically.',
      },
      {
        question: 'Retrosive vs Parabol for small agile teams?',
        answer:
          "For a single team, Retrosive is quicker to get going and the AI summary is the bigger draw day to day. Parabol makes more sense once you're a larger org that actually needs SOC 2 and GitHub sync.",
      },
      {
        question: 'Which tool has better AI for retrospectives?',
        answer:
          "Retrosive, by a decent margin: full summaries, sentiment, patterns across sprints. Parabol's AI is more of a bolt-on meeting summary; retros aren't really where it's focused.",
      },
    ],
    content: buildComparison({
      competitorName: 'Parabol',
      intros: [
        'Parabol markets itself as a meetings platform rather than just a retro tool. Jira and GitHub sync, SOC 2 compliance, the enterprise checklist is checked.',
        "Retrosive plays a different game: same Jira context inside the board, but retro-specific AI is the whole point, not an add-on. You'll also get through setup a lot faster.",
      ],
      featureRows: [
        ['AI retrospective summary', '✅ Full', '⚠️ Limited'],
        ['Cross-sprint pattern analysis', '✅ Yes', '❌ No'],
        ['Jira integration', '✅ Yes', '✅ Yes'],
        ['GitHub integration', 'Not needed', '✅ Yes'],
        ['SOC 2 compliance', '❌ No', '✅ Yes'],
        ['Anonymous mode', '✅ Yes', '✅ Yes'],
        ['Private writing phase', '✅ Yes', '✅ Yes'],
        ['No-account participation', '✅ Yes', '❌ Requires account'],
        ['Setup time (first retro)', '< 2 minutes', '5–10 minutes'],
        ['Canvas-based grouping', '✅ Yes', '⚠️ Structured lists'],
      ],
      focusTitle: 'Where each one actually wins',
      focusParagraphs: [
        "Both connect to Jira, so that's a wash. Retrosive shows sprint issues, velocity, and burndown right next to the board without you tabbing away.",
        "Parabol's edge is pushing actions back into Jira/GitHub and the SOC 2 badge for procurement. Retrosive's edge is that the AI is actually built for retro content: summaries, sentiment, and what keeps repeating across sprints, plus you're in your first retro in under two minutes instead of ten.",
      ],
      chooseCompetitor: [
        'You need SOC 2 today, not on a roadmap',
        'You want action items synced into GitHub, not just Jira',
        "You're running a large program that needs Parabol's org hierarchy tools",
      ],
      chooseRetrosive: [
        'AI-generated summaries are the feature you actually want',
        'Jira sprint context inside the board matters more than GitHub sync',
        "You'd rather be running your first retro in two minutes than ten",
        'You want guests to join without creating an account',
      ],
      ctaText: 'See what an AI-first Jira retro looks like: try Retrosive free',
    }),
  },
  {
    slug: 'retrosive-vs-ludi',
    name: 'Ludi',
    emoji: '🎨',
    hubSummary:
      "Ludi (the Metro Retro rebrand) is a genuinely nice-looking board for every kind of agile ceremony. Retrosive only does one thing, sprint retros, but does it with AI and a smaller bill.",
    hubPitch: 'One tool for every ceremony, or one tool that nails retros specifically.',
    seoTitle: 'Retrosive vs Ludi (Metro Retro): Retrospective Tools Compared',
    seoDescription:
      'Retrosive vs Ludi: retro-specific AI vs a full ceremony suite. Templates, pricing, and who each one actually fits.',
    tags: ['Ludi alternative', 'Metro Retro alternative', 'Ludi vs Retrosive'],
    faqs: [
      {
        question: 'What is the difference between Ludi and Retrosive?',
        answer:
          "Ludi covers the whole agile ceremony menu, planning poker, health checks, retros, workshops. Retrosive does just retros, with AI summaries and cross-sprint tracking, at a lower price since you're not paying for modules you don't use.",
      },
      {
        question: 'Is Ludi the same as Metro Retro?',
        answer: "Yes, Metro Retro renamed itself to Ludi a while back. Retrosive is a separate, newer product built specifically around AI-powered retros.",
      },
      {
        question: 'Ludi vs Retrosive pricing for retros only?',
        answer:
          "If retros are literally all you use it for, Retrosive usually comes out cheaper, the free tier includes AI. Ludi's pricing reflects the fact you're buying a whole ceremony suite whether you use it all or not.",
      },
    ],
    content: buildComparison({
      competitorName: 'Ludi',
      intros: [
        "Ludi is polished, no argument there. It handles retros, planning poker, health checks, and workshops, and the UI is genuinely engaging.",
        "Retrosive picked one lane: sprint retrospectives, with AI doing the summary and pattern-spotting work, and none of the ceremony-suite pricing that comes with covering everything.",
      ],
      featureRows: [
        ['Primary focus', 'Sprint retrospectives + AI', 'Full agile ceremony suite'],
        ['AI retrospective summary', '✅ Yes', '❌ No'],
        ['Cross-sprint pattern analysis', '✅ Yes', '❌ No'],
        ['Retrospective templates', '✅ 12+', '✅ Many'],
        ['Sprint planning / estimation', '❌ No', '✅ Yes'],
        ['Jira integration', '✅ Yes', '✅ Yes'],
        ['Private writing & anonymous voting', '✅ Yes', '✅ Yes'],
        ['Action item tracking', '✅ Yes', '✅ Yes'],
        ['Free tier generosity', '✅ Generous', '⚠️ Limited'],
        ['Starting price (paid)', 'Lower', 'Higher'],
      ],
      focusTitle: 'One tool for everything, or one tool for this',
      focusParagraphs: [
        "If your org wants a single whiteboard for retros, planning, health checks, and workshops, with Jira-linked actions, Ludi handles that well.",
        "If retros are the actual priority, and you'd rather AI handled the writeup and flagged repeating themes, Retrosive gets there for less, since you're not funding ceremony types you never open.",
      ],
      tip: 'Already running everything through Ludi across multiple ceremony types? Probably not worth switching. Buying mainly for retros? You\'re likely overpaying.',
      chooseCompetitor: [
        'You want one whiteboard standardized across many ceremony types',
        'You need retro actions to create Jira tickets natively',
        'You run planning poker, health checks, and workshops in the same product',
      ],
      chooseRetrosive: [
        "You're focused on sprint retros, not the full ceremony menu",
        'You want AI summaries and pattern tracking across sprints',
        "You'd rather not pay for ceremony modules you never touch",
        'You want a fast, retro-specific workflow over a broad whiteboard',
      ],
    }),
  },
  {
    slug: 'retrosive-vs-miro',
    name: 'Miro',
    emoji: '🖼️',
    hubSummary:
      "Miro can do anything, which is exactly the problem for retros: you're building the columns and running the process yourself, every time. Retrosive just already has that part done, plus AI.",
    hubPitch: 'An infinite canvas for everything, or a board that already knows it\'s a retro.',
    seoTitle: 'Retrosive vs Miro: Best Tool for Sprint Retrospectives?',
    seoDescription:
      'Retrosive vs Miro for agile retros: what you get built-in vs what you have to build yourself, plus AI summaries and pricing.',
    tags: ['Miro alternative for retrospectives', 'Miro vs Retrosive', 'retrospective tool'],
    faqs: [
      {
        question: 'Can I use Miro for sprint retrospectives?',
        answer:
          "You can, plenty of teams do. You'll just be building the columns and facilitation flow yourself each time. Retrosive ships that structure ready-made, plus AI summaries afterward.",
      },
      {
        question: 'Is Retrosive a Miro alternative for agile teams?',
        answer:
          "For retros specifically, yes. Retrosive isn't trying to replace Miro for brainstorming or roadmapping, it's built around one thing: getting your retro running fast with less setup and a summary you didn't have to write.",
      },
      {
        question: 'Miro vs Retrosive for remote retrospectives?',
        answer:
          'Both work fine remotely. Retrosive adds the retro-specific stuff Miro leaves to you: private writing before votes go public, anonymous feedback, a visible facilitator timer, and an AI report at the end.',
      },
    ],
    content: buildComparison({
      competitorName: 'Miro',
      intros: [
        "Miro is one of the most-used visual collaboration tools out there, brainstorming, workshops, roadmaps, and yes, sometimes retros.",
        "Retros on Miro work, but you're adapting a general whiteboard: manual columns, manual facilitation, manual summary afterward. Retrosive ships the retro structure and the AI summary already built in.",
      ],
      featureRows: [
        ['Built for sprint retrospectives', '✅ Yes', '❌ General whiteboard'],
        ['Retro templates (ready to run)', '✅ 12+', '⚠️ Community templates'],
        ['Facilitator phases (write → vote → discuss)', '✅ Built-in', '❌ Manual'],
        ['Private writing mode', '✅ Yes', '⚠️ Workarounds'],
        ['Anonymous feedback', '✅ Yes', '⚠️ Limited'],
        ['AI retrospective summary', '✅ Yes', '⚠️ Miro AI (generic)'],
        ['Cross-sprint retro analytics', '✅ Yes', '❌ No'],
        ['Per-seat pricing for retro-only use', 'Lower', 'Higher'],
        ['Learning curve for new facilitators', 'Low', 'Medium–High'],
      ],
      focusTitle: 'Flexible vs already-solved',
      focusParagraphs: [
        "Miro's flexibility is genuinely great for open-ended workshops. For a retro you run every two weeks, that same flexibility means every facilitator quietly reinvents the same board.",
        'Retrosive just has the retro best practices baked in already, timers, phases, voting, actions, and a written summary at the end, so your time goes into the actual conversation instead of the board setup.',
      ],
      chooseCompetitor: [
        "You're already on Miro Enterprise for a dozen other use cases",
        'You need an infinite canvas for open-ended workshops',
        "Your design team lives in Miro for product discovery anyway",
      ],
      chooseRetrosive: [
        'You run retros every sprint and want zero board setup',
        "You want a summary written for you, tuned to retro content",
        "You're tracking the same themes and actions across sprints",
        "You'd rather pay retro-tool pricing than whiteboard-enterprise pricing",
      ],
    }),
  },
  {
    slug: 'retrosive-vs-figjam',
    name: 'FigJam',
    emoji: '🎯',
    hubSummary:
      "FigJam is great if your team already lives in Figma. Retrosive is built for engineering teams who want a real retro process and an AI summary, without a design-tool subscription.",
    hubPitch: 'Design-team whiteboard, or an AI-run retro built for engineering teams.',
    seoTitle: 'Retrosive vs FigJam: Retrospectives for Agile Teams',
    seoDescription:
      "Retrosive vs FigJam for sprint retros: facilitation, AI, pricing, and when a design whiteboard isn't quite the right tool for the job.",
    tags: ['FigJam alternative', 'FigJam retrospective', 'FigJam vs Retrosive'],
    faqs: [
      {
        question: 'Should engineering teams use FigJam for retrospectives?',
        answer:
          "It works if you're already paying for Figma anyway. But engineering teams running a real retro process usually end up wanting Retrosive instead, for the built-in phases and the AI summary neither of which FigJam has.",
      },
      {
        question: 'FigJam vs Retrosive for Scrum Masters?',
        answer:
          "Scrum Masters tend to pick Retrosive for the retro-native templates, anonymous feedback, and the AI insight across sprints. FigJam simply wasn't built with any of that in mind.",
      },
      {
        question: 'Does Retrosive require Figma?',
        answer: "Not at all. Retrosive is its own product with its own board, templates, and Jira integration.",
      },
    ],
    content: buildComparison({
      competitorName: 'FigJam',
      intros: [
        "FigJam is a nice whiteboard bundled into Figma, popular for design critiques and looser team sessions.",
        "For engineering teams running formal sprint retros, though, it's missing the specifics: phased facilitation, real anonymous feedback controls, and any AI tuned to what a retro actually looks like.",
      ],
      featureRows: [
        ['Built for engineering retrospectives', '✅ Yes', '❌ General collaboration'],
        ['Retro facilitation phases', '✅ Yes', '❌ Manual'],
        ['AI sprint retro summary', '✅ Yes', '❌ No'],
        ['Cross-sprint pattern tracking', '✅ Yes', '❌ No'],
        ['Anonymous retro feedback', '✅ Yes', '⚠️ Limited'],
        ['Action item owners & tracking', '✅ Yes', '⚠️ Basic stickies'],
        ['Requires Figma subscription', '❌ No', '✅ Often yes'],
        ['Best for design/product workshops', '⚠️ Adequate', '✅ Excellent'],
      ],
      focusTitle: 'A design tool doing retro duty',
      focusParagraphs: [
        "If your product and design teams already live in Figma, retros there feel familiar, if a bit improvised. You're rebuilding the same columns and timers by hand each time.",
        "Retrosive gives engineering teams a dedicated space instead, with an AI summary after every session and history that carries across sprints, without tying your retro to a design toolchain.",
      ],
      chooseCompetitor: [
        "You're already paying for Figma/FigJam org-wide",
        'Your sessions mix real design exploration with a lighter retro format',
        "Visual polish matters more to you than retro-specific analytics",
      ],
      chooseRetrosive: [
        'Your team runs a formal sprint retro, not a design critique',
        "You'd rather not rebuild the same board by hand every time",
        "You don't want to pay design-tool prices for an engineering ceremony",
        'You want an AI writeup after every session',
      ],
    }),
  },
  {
    slug: 'retrosive-vs-mural',
    name: 'Mural',
    emoji: '🧩',
    hubSummary:
      "Mural is built for big enterprise workshops and it's good at that. Retrosive covers the same retro depth, AI included, for a fraction of what Mural charges.",
    hubPitch: 'Enterprise workshop pricing, or a focused retro tool that costs a lot less.',
    seoTitle: 'Retrosive vs Mural: Agile Retrospective Comparison',
    seoDescription:
      'Retrosive vs Mural for sprint retros: facilitation, AI, pricing, and why a workshop platform and a retro tool solve different problems.',
    tags: ['Mural alternative', 'Mural vs Retrosive', 'retrospective software'],
    faqs: [
      {
        question: 'Is Mural good for sprint retrospectives?',
        answer:
          "It's good at enterprise workshops generally. For a retro you run every sprint, Retrosive is leaner, includes an AI summary, and is priced for agile teams rather than company-wide facilitation.",
      },
      {
        question: 'Mural vs Retrosive cost for one team?',
        answer:
          "For a single team using it mainly for retros, Retrosive is usually cheaper, and the free plan actually covers real use. Mural's pricing assumes broader workshop usage.",
      },
      {
        question: 'Which has better retrospective AI?',
        answer: "Retrosive. Summaries, sentiment, patterns across sprints, all of it. Mural doesn't have retro-specific AI reporting.",
      },
    ],
    content: buildComparison({
      competitorName: 'Mural',
      intros: [
        'Mural is an enterprise collaboration platform with real facilitator depth, aimed at workshops, design thinking, innovation sessions, that scale.',
        "Teams can run retros there, but retro-specific stuff, AI summaries, cross-sprint history, retro-native phases, just isn't where Mural has invested.",
      ],
      featureRows: [
        ['Retrospective-first product design', '✅ Yes', '❌ Workshop-first'],
        ['AI retro summary (retro-trained)', '✅ Yes', '❌ No'],
        ['Cross-sprint retro analytics', '✅ Yes', '❌ No'],
        ['Enterprise workshop facilitation', '⚠️ Adequate', '✅ Excellent'],
        ['Facilitator super-lock / timers for retros', '✅ Yes', '✅ Yes'],
        ['Typical cost for retro-only teams', 'Lower', 'Higher'],
        ['Setup time for weekly sprint retro', '< 2 min', '5+ min'],
      ],
      focusTitle: 'Paying for capability you\'ll never open',
      focusParagraphs: [
        "Mural's facilitation toolkit is genuinely strong for big, professionally-run workshops. For a biweekly sprint retro, most teams end up paying for a lot of that they'll never touch.",
        'Retrosive puts the investment where a Scrum Master actually needs it every sprint: an honest feedback flow and an AI that turns raw notes into a summary and a trend line, without the workshop-platform overhead.',
      ],
      chooseCompetitor: [
        'Your company uses Mural org-wide for innovation and design work',
        'You facilitate mixed workshop formats well beyond agile ceremonies',
        'You already have an enterprise Mural contract and trained facilitators',
      ],
      chooseRetrosive: [
        'Sprint retros are the actual job, not general workshops',
        "You want AI and multi-sprint insight without enterprise whiteboard pricing",
        'Your team is distributed and needs a fast, repeatable retro setup',
      ],
    }),
  },
  {
    slug: 'retrosive-vs-retrium',
    name: 'Retrium',
    emoji: '🏛️',
    hubSummary:
      'Retrium is a mature, guided facilitation platform built around psychological safety and health checks. Retrosive keeps solid facilitation but adds AI summaries and a visual canvas.',
    hubPitch: 'Guided, text-first facilitation, or AI-powered retros on an actual canvas.',
    seoTitle: 'Retrosive vs Retrium: Retrospective Platforms Compared',
    seoDescription:
      'Retrosive vs Retrium: facilitation depth, AI features, enterprise fit, pricing, and which retro tool actually fits your team.',
    tags: ['Retrium alternative', 'Retrium vs Retrosive', 'retrospective facilitation'],
    faqs: [
      {
        question: 'Retrium vs Retrosive for enterprise teams?',
        answer:
          "Retrium's strength is formal facilitation playbooks and health checks at scale. Retrosive brings AI summaries, canvas-based grouping, and a much faster path for a single Scrum team that doesn't need the enterprise apparatus.",
      },
      {
        question: 'Does Retrium have AI retrospective summaries?',
        answer: "No, it doesn't. Retrosive generates the summary, themes, and action items automatically after every session.",
      },
      {
        question: 'Can Retrosive replace Retrium?',
        answer:
          "For teams focused on sprint retros with AI documentation and Jira context, often yes. If you rely on Retrium's enterprise health-check programs specifically, you may want to keep that running separately.",
      },
    ],
    content: buildComparison({
      competitorName: 'Retrium',
      intros: [
        'Retrium is a mature product built around guided facilitation, health checks, and psychological safety, popular with enterprise agile coaches for good reason.',
        "Its structured flows genuinely help less experienced facilitators. Retrosive keeps that same facilitation discipline but adds AI-generated summaries and cross-sprint pattern tracking on a canvas you can actually see.",
      ],
      featureRows: [
        ['Guided facilitation workflows', '✅ Yes', '✅ Yes (mature)'],
        ['Visual canvas board', '✅ Yes', '❌ Text-focused'],
        ['AI retrospective summary', '✅ Yes', '❌ No'],
        ['Cross-sprint pattern analysis', '✅ Yes', '❌ No'],
        ['Team health check surveys', '❌ No', '✅ Yes'],
        ['Enterprise SSO / compliance', '⚠️ Growing', '✅ Yes'],
        ['Anonymous feedback', '✅ Yes', '✅ Yes'],
        ['Pricing for small teams', 'More accessible', 'Higher entry'],
      ],
      focusTitle: 'Standardized playbooks vs a smarter canvas',
      focusParagraphs: [
        "Retrium makes the most sense when an org wants standardized facilitation playbooks and health-check programs across a lot of teams.",
        'Retrosive is the better fit when your facilitators want to keep working on a real canvas while AI handles the writeup, flags what keeps repeating, and gives a manager an actual longitudinal view.',
      ],
      chooseCompetitor: [
        "You're standardizing an enterprise agile transformation on Retrium's methodology",
        'You need formal team health-check programs in the same tool',
        'Your coaches prefer a text-first, tightly scripted retro format',
      ],
      chooseRetrosive: [
        'You want canvas flexibility and AI, not one or the other',
        "You're tired of writing the retro summary by hand",
        'You need to track unresolved actions across sprints',
        "Retrium's pricing feels heavy for a team your size",
      ],
    }),
  },
  {
    slug: 'retrosive-vs-lucidspark',
    name: 'Lucidspark',
    emoji: '💡',
    hubSummary:
      "Lucidspark is a brainstorming board that lives alongside Lucidchart. It's not built for retros specifically, which is exactly where Retrosive's AI and ready-made templates come in.",
    hubPitch: 'Lucid-suite brainstorming, or a retro tool that\'s ready before you open it.',
    seoTitle: 'Retrosive vs Lucidspark: Sprint Retrospective Tools',
    seoDescription:
      'Retrosive vs Lucidspark for agile retros: AI features, facilitation, pricing, and which one actually fits a Scrum team.',
    tags: ['Lucidspark alternative', 'Lucidspark retrospective', 'Lucidspark vs Retrosive'],
    faqs: [
      {
        question: 'Lucidspark vs Retrosive for agile retros?',
        answer:
          "Lucidspark is a general brainstorming board in the Lucid suite. Retrosive is built only for sprint retros, templates, facilitation phases, and AI reporting included.",
      },
      {
        question: 'Do I need Lucidchart to use Retrosive?',
        answer: "No, they're unrelated. Teams usually move to Retrosive precisely because they want retro-specific features without paying for the whole Lucid suite.",
      },
      {
        question: 'Which tool is faster to start a retrospective?',
        answer:
          'Retrosive, clearly: create a board, share the link, go, usually under two minutes. Lucidspark means building or importing a layout every time.',
      },
    ],
    content: buildComparison({
      competitorName: 'Lucidspark',
      intros: [
        "Lucidspark is Lucid Software's brainstorming space, often paired with Lucidchart for diagrams and process mapping.",
        "You can run a retro on a Lucidspark board, sure, but it's optimized for open-ended ideation, not a recurring sprint ceremony with an AI writeup at the end.",
      ],
      featureRows: [
        ['Purpose-built for sprint retros', '✅ Yes', '❌ Brainstorming-first'],
        ['Retro templates ready to launch', '✅ 12+', '⚠️ Generic'],
        ['Facilitation phases for retros', '✅ Built-in', '❌ Manual'],
        ['AI retrospective summary', '✅ Yes', '❌ No'],
        ['Cross-sprint analytics', '✅ Yes', '❌ No'],
        ['Lucidchart integration', '❌ No', '✅ Yes'],
        ['Cost for retro-only teams', 'Lower', 'Higher'],
      ],
      focusTitle: 'A diagramming tool doing double duty',
      focusParagraphs: [
        "If your org already standardizes on Lucid for architecture diagrams, that makes sense. Retros there just mean rebuilding a custom template every single sprint.",
        'Retrosive skips that setup tax entirely and adds an AI summary plus history across sprints, built for Scrum Masters, not diagram authors.',
      ],
      chooseCompetitor: [
        'Your team is deep into Lucidchart/Lucidspark for technical diagrams',
        'You want one Lucid bill covering brainstorming and documentation',
        'You run broad ideation sessions well beyond agile ceremonies',
      ],
      chooseRetrosive: [
        "You want a retro-specific tool without paying for the Lucid suite",
        'You want the AI to write the summary and track sprint-over-sprint',
        "You'd rather not rebuild a retro board from scratch every sprint",
      ],
    }),
  },
  {
    slug: 'retrosive-vs-microsoft-whiteboard',
    name: 'Microsoft Whiteboard',
    emoji: '🪟',
    hubSummary:
      "Microsoft Whiteboard is free with M365, which is its whole appeal, but there's no retro structure at all. Retrosive adds the templates, phases, and AI that make the session worth having.",
    hubPitch: 'Free and blank, or structured with an AI summary at the end.',
    seoTitle: 'Retrosive vs Microsoft Whiteboard: Agile Retrospectives',
    seoDescription:
      'Retrosive vs Microsoft Whiteboard for sprint retros: what a blank canvas is missing, and what AI and templates add for remote teams.',
    tags: ['Microsoft Whiteboard alternative', 'Teams retrospective tool', 'M365 retro'],
    faqs: [
      {
        question: 'Can I run retrospectives in Microsoft Teams Whiteboard?',
        answer:
          "You can, on a blank canvas, but there's no retro phasing, no anonymous mode, no AI summary. Retrosive adds all of that for Teams-centric orgs that still want the structure.",
      },
      {
        question: 'Is Retrosive free compared to Microsoft Whiteboard?',
        answer:
          "Whiteboard comes free with M365. Retrosive has its own free tier too, AI reports and templates included, and a lot of teams run it alongside M365 once retro quality starts to matter.",
      },
      {
        question: 'Best retrospective tool for Microsoft Teams users?',
        answer:
          "Once a blank whiteboard stops being enough, teams move to Retrosive for private writing, voting, action tracking, Jira context, and the AI summary, while still just dropping the link in Teams chat.",
      },
    ],
    content: buildComparison({
      competitorName: 'Microsoft Whiteboard',
      intros: [
        "Microsoft Whiteboard ships with M365, so for Teams-centric orgs it's the default choice for an informal retro on a blank canvas.",
        "What it saves in license cost, facilitators usually pay back in time: no retro phases, no anonymous mode, no AI summary, nothing carried over from last sprint.",
      ],
      featureRows: [
        ['Included with Microsoft 365', '❌ Separate product', '✅ Yes'],
        ['Retro-specific templates', '✅ 12+', '❌ Manual setup'],
        ['Private writing phase', '✅ Yes', '❌ No'],
        ['Anonymous retro feedback', '✅ Yes', '❌ No'],
        ['Facilitator timer visible to all', '✅ Yes', '⚠️ Basic'],
        ['AI retrospective summary', '✅ Yes', '❌ No'],
        ['Cross-sprint retro analytics', '✅ Yes', '❌ No'],
        ['Action item tracking in retro', '✅ Yes', '⚠️ Manual stickies'],
      ],
      focusTitle: 'Free canvas, or a retro that actually goes somewhere',
      focusParagraphs: [
        "Whiteboard is fine for ad-hoc brainstorming inside Teams. A real sprint retro needs a bit more: phases that stop groupthink, actual voting, and a summary that outlives the meeting.",
        "Retrosive layers those on top of real-time collaboration, plus an AI writeup that managers will genuinely read.",
      ],
      tip: "A lot of Teams-heavy orgs start on Whiteboard because it's free, then move to Retrosive once retro quality and the AI report actually start to matter.",
      chooseCompetitor: [
        'You have zero budget beyond existing M365 licenses',
        "Your retros are informal and infrequent, no real facilitation needed",
        'Your org blocks additional SaaS tools outright',
      ],
      chooseRetrosive: [
        'You run a structured retro every sprint',
        'Your team is remote and needs private writing plus anonymous feedback',
        'You want an AI summary and trend reports a manager will actually read',
        "You've outgrown blank-canvas retros that never lead anywhere",
      ],
    }),
  },
  {
    slug: 'retrosive-vs-teamretro',
    name: 'TeamRetro',
    emoji: '🏢',
    hubSummary:
      "TeamRetro is built for enterprise, multi-team dashboards and SSO. Retrosive is AI-first and has a genuinely usable free tier for a team that isn't managing twenty squads.",
    hubPitch: 'Enterprise health dashboards, or AI retros any team can start today.',
    seoTitle: 'Retrosive vs TeamRetro: Which Retro Tool Fits Your Team?',
    seoDescription:
      'Retrosive vs TeamRetro: enterprise analytics vs AI-first retros, pricing, SSO, and whether you actually need enterprise scale.',
    tags: ['TeamRetro alternative', 'TeamRetro vs Retrosive', 'enterprise retrospective'],
    faqs: [
      {
        question: 'TeamRetro vs Retrosive for a single Scrum team?',
        answer:
          "For one team, Retrosive is faster to adopt and includes the AI summary out of the box, plus a real free tier. TeamRetro is built for orgs managing a lot of teams at once with centralized analytics.",
      },
      {
        question: 'Does TeamRetro have AI?',
        answer: "TeamRetro is focused on facilitation and multi-team reporting, not AI-generated summaries. Retrosive is AI-first for every session.",
      },
      {
        question: 'When should I choose TeamRetro over Retrosive?',
        answer:
          "If you need enterprise-wide health checks, SSO, and org-level analytics across twenty-plus teams, TeamRetro's built for that. If you just want a fast, AI-assisted retro at the team level, Retrosive gets you there without the overhead.",
      },
    ],
    content: buildComparison({
      competitorName: 'TeamRetro',
      intros: [
        'TeamRetro is an established enterprise platform with multi-team analytics, health checks, and SSO, aimed squarely at orgs running a lot of squads at once.',
        "Retrosive covers similar facilitation ground for a single team, plus AI summaries and cross-sprint insight, with onboarding that suits a startup or mid-size eng org rather than an enterprise procurement process.",
      ],
      featureRows: [
        ['AI retrospective summary', '✅ Yes', '❌ No'],
        ['Cross-sprint pattern analysis', '✅ Yes', '⚠️ Analytics dashboards'],
        ['Multi-team org-wide reporting', '⚠️ Growing', '✅ Yes'],
        ['Team health check surveys', '❌ No', '✅ Yes'],
        ['Enterprise SSO', '⚠️ Roadmap', '✅ Yes'],
        ['Meaningful free tier', '✅ Yes', '❌ No'],
        ['Time to first retro', '< 2 minutes', '10+ minutes'],
        ['Visual canvas retrospectives', '✅ Yes', '⚠️ Structured columns'],
      ],
      focusTitle: 'Enterprise scale, or just getting the team retro right',
      focusParagraphs: [
        "TeamRetro is the right call when an agile coach needs centralized reporting across dozens of teams and a formal health-check program to go with it.",
        "Retrosive is the right call when a Scrum Master just wants a fast, engaging retro with an AI writeup, no procurement cycle, no per-seat minimums.",
      ],
      chooseCompetitor: [
        'You run 20+ teams and need centralized retro analytics',
        'You need SSO and formal health-check tooling today',
        'An agile coach is standardizing retro process across a whole division',
      ],
      chooseRetrosive: [
        'A single team or department, not an enterprise rollout',
        'You want AI summaries without enterprise pricing',
        "You're a startup or scale-up and need a real free tier to start today",
        'You prefer canvas-based retros over corporate-looking columns',
      ],
    }),
  },
  {
    slug: 'retrosive-vs-neatro',
    name: 'Neatro',
    emoji: '🎯',
    hubSummary:
      "Neatro's whole thing is variety, 30+ templates, icebreakers, team radars, and it's free. Retrosive picked fewer templates but added an AI that writes the summary and tracks patterns for you.",
    hubPitch: 'A big library of fun templates, or AI that does the write-up automatically.',
    seoTitle: 'Retrosive vs Neatro: Which Retrospective Tool Is Better in 2026?',
    seoDescription:
      'Retrosive vs Neatro: AI summaries, template variety, team radars, and which retro tool actually saves your team time.',
    tags: ['Neatro alternative', 'Neatro vs Retrosive', 'retrospective tool comparison'],
    faqs: [
      {
        question: 'Is Retrosive a good Neatro alternative?',
        answer:
          "Yes, it covers the same basics, templates, anonymous feedback, action tracking, and adds an AI-generated summary plus pattern tracking across sprints, which Neatro doesn't have.",
      },
      {
        question: 'Does Neatro have AI retrospective summaries?',
        answer:
          "It doesn't. Neatro's focus is templates, team radars, and ROTI surveys. Retrosive writes the summary for you automatically after every session, themes, sentiment, and actions included.",
      },
      {
        question: 'Does Neatro have more templates than Retrosive?',
        answer:
          'It does, 30+ templates including novelty ones like "Retro Football Club." Retrosive ships a tighter 12+ set covering what teams actually run most, Start Stop Continue, 4Ls, Mad Sad Glad, Sailboat, with AI reporting on every one of them.',
      },
      {
        question: 'Can I migrate from Neatro to Retrosive?',
        answer: 'Yes. Create a board, pick a template, share the link, most teams are running their first retro in under two minutes with no credit card required.',
      },
    ],
    content: buildComparison({
      competitorName: 'Neatro',
      intros: [
        'Neatro is a free, template-heavy retro tool used by teams at Adobe, Mattermost, and Capgemini. Its thing is variety: 30+ boards, icebreakers, team health radars.',
        "Retrosive went narrower on purpose: fewer templates, but AI that turns every session into a shareable summary automatically, themes, sentiment, and actions included, without a facilitator writing any of it by hand.",
      ],
      featureRows: withCompetitorColumn(STANDARD_RETRO_ROWS, 'Neatro', [
        '✅ Yes',
        '✅ Yes',
        '✅ Yes',
        '✅ Yes',
        '✅ 30+',
        '✅ Yes',
        '✅ Yes',
        '❌ No',
        '❌ No',
        '✅ Yes',
        '2–3 minutes',
      ]),
      focusTitle: 'Wide template library, or the write-up done for you',
      focusParagraphs: [
        "Neatro goes wide: 30+ templates, 200+ icebreaker questions, team radar health checks. Great for keeping retros fresh, but the summary and action rollup are still on you every single time.",
        "Retrosive goes deep instead: after each retro, an AI summary captures the themes, sentiment, and risks, and flags recurring blockers across sprints, so the Scrum Master spends less time writing reports and more time acting on them.",
      ],
      tip: "Want a big template library and novelty formats to keep things fun? Neatro's a solid pick. Want the AI to handle the writeup and catch patterns across sprints? Retrosive saves more time.",
      chooseCompetitor: [
        'You want the widest possible variety of templates and icebreakers',
        'Built-in team health radars and ROTI surveys matter to you',
        "You don't need an AI-generated summary",
      ],
      chooseRetrosive: [
        'You want the AI summary written for you, out of the box',
        "You want recurring blockers flagged without extra tooling",
        'A tighter, sharper template set beats dozens of variants for you',
        'A real free plan with no credit card matters',
      ],
    }),
  },
];

/** Hub page FAQs: used for /alternatives rich results */
export const alternativesHubFaqs: FaqItem[] = [
  {
    question: 'What is the best EasyRetro alternative in 2026?',
    answer:
      'Retrosive is a strong pick if you want the AI to write your retro summary for you, plus cross-sprint tracking and real-time canvas collaboration, on a free plan that actually covers real use.',
  },
  {
    question: 'What is the best Miro alternative for sprint retrospectives?',
    answer:
      "Retrosive, since it's built specifically for agile retros instead of adapted from a general whiteboard. Templates, facilitator phases, anonymous feedback, and an AI report all come ready to go.",
  },
  {
    question: 'How do I compare retrospective tools?',
    answer:
      'Check the comparison hub at retrosive.com/alternatives for honest, side-by-side breakdowns against Parabol, Ludi, EasyRetro, Retrium, FigJam, Mural, Lucidspark, Microsoft Whiteboard, Neatro, and TeamRetro.',
  },
];

export function getAlternative(slug: string): AlternativeCompetitor | undefined {
  return alternatives.find((a) => a.slug === slug);
}

export function getAlternativeSlug(competitorKey: string): string {
  return `retrosive-vs-${competitorKey}`;
}
